import {
  CarManufacturerDto,
  SearchMetadataResponse,
  VehicleTagModel,
  VehicleTypeModel,
} from "api"

interface FilterOptions {
  selectedTypes: number[]
  selectedTags: number[]
}

/**
 * Get manufacturers from selected types
 */
function getManufacturersFromTypes(
  types: VehicleTypeModel[],
  selectedTypes: number[],
): CarManufacturerDto[] {
  const matchingTypes = types.filter((type) =>
    selectedTypes.includes(type.vehicleTypeForWebsiteCode),
  )

  const allManufacturers: CarManufacturerDto[] = []
  matchingTypes.forEach((type) => {
    allManufacturers.push(...type.manufacturers)
  })

  return allManufacturers
}

/**
 * Get manufacturers from selected tags
 */
function getManufacturersFromTags(
  tags: VehicleTagModel[],
  selectedTags: number[],
): CarManufacturerDto[] {
  const matchingTags = tags.filter((tag) =>
    selectedTags.includes(tag.vehicleTagForWebsiteCode),
  )

  const allManufacturers: CarManufacturerDto[] = []
  matchingTags.forEach((tag) => {
    allManufacturers.push(...tag.manufacturers)
  })

  return allManufacturers
}

/**
 * Intersect manufacturer sets on both manufacturer code and model code.
 *
 * Matching on the manufacturer alone is not enough: the search API filters
 * vehicles, so a brand only stays selectable when the SAME model carries every
 * selected facet. BMW listing X5 under the "7 מקומות" tag and Z4 under the
 * "ספורט" type means no BMW is both, and offering BMW there sends the user to
 * an empty result page (END-268). A manufacturer whose models do not overlap
 * across the sets is therefore dropped, and the survivors keep only the models
 * present in every set so the model dropdown stays in sync too.
 */
function intersectManufacturers(
  manufacturerSets: CarManufacturerDto[][],
): CarManufacturerDto[] {
  if (manufacturerSets.length === 0) return []

  return manufacturerSets.reduce((intersection, set) => {
    const byCode = new Map(
      set.map((manufacturer) => [manufacturer.code, manufacturer]),
    )

    return intersection.reduce<CarManufacturerDto[]>((kept, manufacturer) => {
      const counterpart = byCode.get(manufacturer.code)
      if (!counterpart) return kept

      const counterpartModels = new Set(
        counterpart.models.map((model) => model.modelCode),
      )
      const models = manufacturer.models.filter((model) =>
        counterpartModels.has(model.modelCode),
      )
      if (models.length > 0) kept.push({ ...manufacturer, models })

      return kept
    }, [])
  })
}

/**
 * Collapse repeated manufacturers into one entry per code, unioning their models.
 * Used per facet group: selecting several types (or several tags) widens the
 * brands and models on offer, so within one group the lists are unioned.
 */
function mergeManufacturerModels(
  manufacturers: CarManufacturerDto[],
): CarManufacturerDto[] {
  const manufacturerMap = new Map<number, CarManufacturerDto>()

  manufacturers.forEach((manufacturer) => {
    const existing = manufacturerMap.get(manufacturer.code)
    if (existing) {
      // Merge models - combine and deduplicate
      const modelCodes = new Set(existing.models.map((m) => m.modelCode))
      manufacturer.models.forEach((model) => {
        if (!modelCodes.has(model.modelCode)) {
          existing.models.push(model)
          modelCodes.add(model.modelCode)
        }
      })
    } else {
      // First occurrence - clone to avoid mutation
      manufacturerMap.set(manufacturer.code, {
        ...manufacturer,
        models: [...manufacturer.models],
      })
    }
  })

  return Array.from(manufacturerMap.values())
}

/**
 * Filter manufacturers based on selected types and tags.
 *
 * The metadata passed in is already scoped to the correct deal type (new/used) and
 * carType by the server, so new-vs-used filtering is NOT done here anymore — the
 * server's deal-type-scoped metadata slice is the single source of truth. This
 * function only narrows the already-correct manufacturer list by the user's selected
 * types/tags, returning the intersection.
 *
 * Types and tags are combined the way the search API combines them — a vehicle has
 * to satisfy both — so the intersection is taken per model, not per manufacturer.
 */
export function filterManufacturersBySelection(
  metadata: SearchMetadataResponse,
  options: FilterOptions,
): CarManufacturerDto[] {
  const { selectedTypes, selectedTags } = options

  // Collect manufacturer sets to intersect
  const manufacturerSets: CarManufacturerDto[][] = []

  // Filter by types (if any selected). A selection that matches nothing is kept
  // as an empty set on purpose — skipping it would silently drop the filter and
  // offer brands the search API cannot return.
  if (selectedTypes.length > 0) {
    manufacturerSets.push(
      mergeManufacturerModels(
        getManufacturersFromTypes(metadata.types || [], selectedTypes),
      ),
    )
  }

  // Filter by tags (if any selected)
  if (selectedTags.length > 0) {
    manufacturerSets.push(
      mergeManufacturerModels(
        getManufacturersFromTags(metadata.tags || [], selectedTags),
      ),
    )
  }

  // No type/tag filters applied - return all manufacturers from the (already
  // deal-type-scoped) metadata.
  if (manufacturerSets.length === 0) {
    return metadata.manufacturers || []
  }

  // Each set already holds one entry per manufacturer with its models unioned
  // across the selected types (or tags), so the intersection is the final list.
  return intersectManufacturers(manufacturerSets)
}
